var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * Authority record types — the frontend mirror of the backend enum
 * `RecordType.java` in mod-czna-authorities.
 *
 * <p><b>This list must equal the Java enum, member for member.</b> Three maps in
 * this module are declared `Record<RecordType, …>` (visual config, icons,
 * indicator defaults), so TypeScript's exhaustiveness check is the only
 * automatic guard that a newly catalogued type gets a colour, an icon and
 * indicators before it can reach a screen. That guard is worth nothing if the
 * list itself is stale — which is exactly what CZNA-457 found: the enum had
 * drifted into carrying 12 vocabulary codes the backend had already removed
 * while missing two types the backend had gained. `recordTypeParity.test.ts`
 * now reads `RecordType.java` and fails on any divergence.
 *
 * <p><b>Vocabulary codes do not belong here.</b> `record.type` is a plain String
 * on the backend and also holds ~27 `VOCABULARY_*` codes, but since
 * CZNA-392/393/397 their identity is DATA — rows in the `vocabulary_type`
 * registry, read at runtime via `useVocabularyTypes()` / GET
 * /czna-authorities/vocabulary-types. Adding a vocabulary type is an INSERT,
 * not an enum change; anything that needs the full set of `record.type` values
 * must union this enum with the registry (see `AuthoritiesList`'s type filter).
 */
export var RecordType;
(function (RecordType) {
    RecordType["PERSONAL"] = "PERSONAL";
    RecordType["CORPORATE"] = "CORPORATE";
    RecordType["MEETING"] = "MEETING";
    RecordType["UNIFORM_TITLE"] = "UNIFORM_TITLE";
    RecordType["CHRONOLOGICAL"] = "CHRONOLOGICAL";
    RecordType["SUBJECT"] = "SUBJECT";
    RecordType["GEOGRAPHIC"] = "GEOGRAPHIC";
    // CZNA-181: old-print city authority (Aleph prefix pv, MARC 195).
    RecordType["OLD_PRINT_CITY"] = "OLD_PRINT_CITY";
    RecordType["GENRE_FORM"] = "GENRE_FORM";
    /**
     * TASK-140: conspect group (Aleph prefix `sk`, MARC 190) — NK's Aleph-native
     * subject grouping, accepted on raw-XML import though it is not a standard
     * MARC 21 authority tag.
     *
     * <p><b>CZNA-1087 — the one type that is TWO record shapes.</b> 598 of the 624
     * live records are a *skupina Konspektu* (MDT notation in `190$a`, parent
     * category in `$9`); the other 26 are a *předmětová kategorie* (`190$k`, no
     * notation). The split is complementary, so `190` is where the shapes part,
     * and the ontology states it as `exactlyOneOf ["9","k"]`. Two packaged form
     * configs follow it — `conspect-group.json` (skupina, the default) and
     * `conspect-group.kategorie.json` — and `matchTemplateShape` picks between
     * them from the record's own `190`. This used to read "import-only […] so it
     * has no form config"; both halves are now false.
     */
    RecordType["CONSPECT_GROUP"] = "CONSPECT_GROUP";
    /**
     * CZNA-422: name-title authority ("autor–dílo") — a name heading plus the
     * title of a work, carried in the SAME 1XX as the name portion.
     *
     * <p><b>The one type that owns no MARC tag.</b> Its heading lives in 100, 110
     * or 111 depending on the name portion, so the tag is a property of the
     * record, not of the type — read it off the record's own fields, never from
     * this constant. `AUTHORITY_TYPE_CONFIG[AUTHOR_WORK].marcTag` is therefore
     * `null` and the tag getters throw, mirroring the backend's
     * `MarcRecordParser#marcTagForType`, which rejects it rather than returning a
     * plausible-looking "100".
     */
    RecordType["AUTHOR_WORK"] = "AUTHOR_WORK";
})(RecordType || (RecordType = {}));
export var HeadingType;
(function (HeadingType) {
    HeadingType["PREFERRED"] = "PREFERRED";
    HeadingType["VARIANT"] = "VARIANT";
    HeadingType["RELATED"] = "RELATED";
    HeadingType["LANGUAGE_VARIANT"] = "LANGUAGE_VARIANT";
    HeadingType["EXCLUDED_FORM"] = "EXCLUDED_FORM";
})(HeadingType || (HeadingType = {}));
// --- Proposal review types ---
export var RejectionReason;
(function (RejectionReason) {
    RejectionReason["DUPLICATE"] = "DUPLICATE";
    RejectionReason["INSUFFICIENT_DATA"] = "INSUFFICIENT_DATA";
    RejectionReason["NOT_AUTHORITY"] = "NOT_AUTHORITY";
    RejectionReason["INCORRECT_DATA"] = "INCORRECT_DATA";
    RejectionReason["OTHER"] = "OTHER";
})(RejectionReason || (RejectionReason = {}));
// --- Proposal types ---
export var ProposalStatus;
(function (ProposalStatus) {
    ProposalStatus["DRAFT"] = "DRAFT";
    /**
     * Submitted, awaiting the FIRST review. For a two-stage authority type
     * (see `TWO_STAGE_APPROVAL_TYPES`) that reviewer is the local supervisor,
     * whose approval forwards the proposal rather than publishing it.
     */
    ProposalStatus["PENDING_REVIEW"] = "PENDING_REVIEW";
    /** CZNA-552 — passed local review, awaiting the NK supervisor's final approval. */
    ProposalStatus["PENDING_NATIONAL_REVIEW"] = "PENDING_NATIONAL_REVIEW";
    ProposalStatus["CHANGES_REQUESTED"] = "CHANGES_REQUESTED";
    ProposalStatus["REJECTED"] = "REJECTED";
    ProposalStatus["APPROVED"] = "APPROVED";
})(ProposalStatus || (ProposalStatus = {}));
/**
 * CZNA-552 — authority types whose proposals pass a local review before the
 * national one: the subject-authority group NK asked to keep uniform (2026-08-14).
 *
 * <p>CZNA-581 / v3.1 §2 (2026-09-02) moved GEOGRAPHIC and OLD_PRINT_CITY off this list —
 * they now publish immediately ("Publikovat") instead. Mirrors
 * `ApprovalWorkflow.TWO_STAGE_TYPES` in mod-czna-authorities; the backend is authoritative
 * and rejects anything this copy gets wrong, so the cost of drift is a button in the wrong
 * place, not bad data. Pinned by a test (`twoStageApprovalParity.test.ts`) so the two
 * cannot part silently.
 */
export var TWO_STAGE_APPROVAL_TYPES = [
    RecordType.SUBJECT,
    RecordType.GENRE_FORM,
    RecordType.CHRONOLOGICAL,
    RecordType.CONSPECT_GROUP,
];
export var ProposalType;
(function (ProposalType) {
    ProposalType["CREATE_NEW"] = "CREATE_NEW";
    ProposalType["UPDATE_EXISTING"] = "UPDATE_EXISTING";
})(ProposalType || (ProposalType = {}));
/**
 * Does this proposal need a local review before the national one? Without `proposalType`,
 * the authority type's own shape. CZNA-1526: a change of a published authority
 * (`UPDATE_EXISTING`) is always two-stage, whatever the type — mirrors
 * `ApprovalWorkflow.isTwoStage(String, ProposalType)`.
 */
export var isTwoStageApproval = function (recordType, proposalType) {
    return proposalType === ProposalType.UPDATE_EXISTING
        || (!!recordType && TWO_STAGE_APPROVAL_TYPES.includes(recordType));
};
/**
 * CZNA-581 — does this authority type publish immediately ("Publikovat"), or does it
 * review first and publish as the last step of approval ("Odeslat ke schválení")? The
 * logical negation of {@link isTwoStageApproval}, under the name the question actually
 * asks — mirrors `ApprovalWorkflow#publishesImmediately`.
 *
 * <p>Only meaningful for a CREATE_NEW proposal: an UPDATE_EXISTING proposal always goes
 * through review regardless of type (v3.1 §8), so callers must check
 * `proposalType === ProposalType.CREATE_NEW` themselves before branching on this.
 */
export var publishesImmediately = function (recordType) {
    return !isTwoStageApproval(recordType);
};
/** Every state in which a proposal is waiting for some reviewer. */
export var PENDING_PROPOSAL_STATUSES = [
    ProposalStatus.PENDING_REVIEW,
    ProposalStatus.PENDING_NATIONAL_REVIEW,
];
/**
 * Maps a WorkflowTab to the API query parameters needed for fetching.
 */
export var TAB_API_PARAMS = {
    DRAFT: { status: ProposalStatus.DRAFT },
    PENDING_NEW: { status: ProposalStatus.PENDING_REVIEW, proposalType: ProposalType.CREATE_NEW },
    PENDING_UPDATE: { status: ProposalStatus.PENDING_REVIEW, proposalType: ProposalType.UPDATE_EXISTING },
    // CZNA-552 — deliberately NOT split into new/update the way PENDING_REVIEW is. The split
    // exists because NK asked to see new terms apart from updates in the queue they work in
    // daily; nobody has worked in the national queue yet, and eight tabs is a real cost to
    // impose on a guess. Splitting it later is one more line here plus a label.
    PENDING_NATIONAL: { status: ProposalStatus.PENDING_NATIONAL_REVIEW },
    CHANGES_REQUESTED: { status: ProposalStatus.CHANGES_REQUESTED },
    REJECTED: { status: ProposalStatus.REJECTED },
    APPROVED: { status: ProposalStatus.APPROVED },
};
export var WORKFLOW_TABS = [
    'DRAFT',
    'PENDING_NEW',
    'PENDING_UPDATE',
    'PENDING_NATIONAL',
    'CHANGES_REQUESTED',
    'REJECTED',
    'APPROVED',
];
/**
 * Does the backend say this user may take this action on this proposal right now?
 *
 * <p>An absent list means "nothing" rather than "everything": a response that did not
 * carry the field cannot be evidence of permission, and drawing a button on that basis is
 * how the pre-CZNA-598 predicates produced clicks that could only ever answer 403.
 */
export var proposalAllows = function (proposal, action) { var _a; return !!((_a = proposal === null || proposal === void 0 ? void 0 : proposal.actions) === null || _a === void 0 ? void 0 : _a.includes(action)); };
/**
 * CZNA-593 — the ownership dimension of the proposal list. The list deliberately shows
 * every library's proposals by default (agreed with NK for the shared overview); this
 * narrows it without changing that default.
 *
 * <p>ORGANIZATION is membership (`proposal.organization_id`), never permission scope — a
 * National Library cataloguer with a national-level role still gets exactly NK's rows.
 */
export var PROPOSAL_OWNERSHIP_FILTERS = ['ALL', 'MINE', 'ORGANIZATION'];
export var DEFAULT_PROPOSAL_OWNERSHIP_FILTER = 'ALL';
export var isProposalOwnershipFilter = function (value) {
    return typeof value === 'string'
        && PROPOSAL_OWNERSHIP_FILTERS.includes(value);
};
/**
 * CZNA-553 — the review stages a proposal on the approval sheet can be waiting in.
 *
 * <p>Mirror of `ApprovalWorkflow.PENDING_STATES` on the Java side, and the single list the
 * sheet's stage labels are enumerated from. It exists so that adding a further review stage
 * cannot reach the paper as a raw message id: `proposalReportMessages.test.ts` requires a
 * label for every entry here, and `ProposalStatus` is exhaustively partitioned by the type
 * assertion below, so a new constant has to be classified rather than forgotten.
 */
export var PENDING_APPROVAL_STAGES = [
    ProposalStatus.PENDING_REVIEW,
    ProposalStatus.PENDING_NATIONAL_REVIEW,
];
/**
 * The statuses that are NOT a pending review stage. Nothing reads this at runtime; it is
 * here so that TypeScript rejects a new `ProposalStatus` constant that is neither listed
 * above nor listed here — which is what makes the enumeration above exhaustive by
 * construction rather than by comment.
 */
export var SETTLED_PROPOSAL_STATUSES = [
    ProposalStatus.DRAFT,
    ProposalStatus.CHANGES_REQUESTED,
    ProposalStatus.REJECTED,
    ProposalStatus.APPROVED,
];
/** Compile-time completeness check: every ProposalStatus is in exactly one of the two. */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
var ALL_STATUSES_PARTITIONED = __assign(__assign({}, Object.fromEntries(PENDING_APPROVAL_STAGES.map(function (s) { return [s, true]; }))), Object.fromEntries(SETTLED_PROPOSAL_STATUSES.map(function (s) { return [s, true]; })));
/**
 * Folds the backend's (status, proposalType) groups onto the seven workflow tabs.
 *
 * A tab that pins proposalType (PENDING_NEW / PENDING_UPDATE) takes only the matching
 * group; every other tab sums across both proposal types, because it does not divide by
 * them. Groups the backend omitted — no rows — read as 0, so the caller never has to tell
 * an absent group from an explicit zero. Hiding the badge itself is a separate, explicit
 * decision in ProposalsList (`count > 0`), not a consequence of this.
 */
export var foldProposalCountsByTab = function (counts) {
    var byTab = Object.fromEntries(WORKFLOW_TABS.map(function (tab) { return [tab, 0]; }));
    counts.forEach(function (_a) {
        var status = _a.status, proposalType = _a.proposalType, count = _a.count;
        WORKFLOW_TABS.forEach(function (tab) {
            var tabParams = TAB_API_PARAMS[tab];
            if (tabParams.status !== status)
                return;
            // A tab without its own proposalType spans both, so it absorbs every group of
            // its status; one that pins the type takes only its own.
            if (tabParams.proposalType && tabParams.proposalType !== proposalType)
                return;
            byTab[tab] += count;
        });
    });
    return byTab;
};
/**
 * Supported language codes (ISO 639-1) for vocabulary translations.
 * `cs` is the primary (required) language; `en` is optional.
 * Hardcoded in Phase 1 — Phase 2 will move this to system configuration.
 */
export var SUPPORTED_LANGUAGES = ['cs', 'en'];
/**
 * Configuration for vocabulary types.
 * Defines which types require a code field and their translation keys.
 */
export var VOCABULARY_TYPE_CONFIG = {
    VOCABULARY_LANGUAGE: { labelKey: 'ui-czna-authorities.vocabularyType.language', codeRequired: true },
    VOCABULARY_NAME_FORM: { labelKey: 'ui-czna-authorities.vocabularyType.nameForm', codeRequired: false },
    VOCABULARY_TITLE: { labelKey: 'ui-czna-authorities.vocabularyType.title', codeRequired: false },
    VOCABULARY_GENERAL_QUALIFIER: { labelKey: 'ui-czna-authorities.vocabularyType.generalQualifier', codeRequired: false },
    VOCABULARY_ACQUIRED_INFO: { labelKey: 'ui-czna-authorities.vocabularyType.acquiredInfo', codeRequired: false },
    VOCABULARY_EXPLANATORY_PHRASE: { labelKey: 'ui-czna-authorities.vocabularyType.explanatoryPhrase', codeRequired: false },
    VOCABULARY_ACADEMIC_TITLE: { labelKey: 'ui-czna-authorities.vocabularyType.academicTitle', codeRequired: false },
    VOCABULARY_NOBLE_TITLE: { labelKey: 'ui-czna-authorities.vocabularyType.nobleTitle', codeRequired: false },
    VOCABULARY_SOVEREIGN_TITLE: { labelKey: 'ui-czna-authorities.vocabularyType.sovereignTitle', codeRequired: false },
    VOCABULARY_RELIGIOUS_ORDER: { labelKey: 'ui-czna-authorities.vocabularyType.religiousOrder', codeRequired: false },
    VOCABULARY_QUALIFIER_368C: { labelKey: 'ui-czna-authorities.vocabularyType.qualifier368c', codeRequired: false },
    VOCABULARY_GENDER: { labelKey: 'ui-czna-authorities.vocabularyType.gender', codeRequired: false },
    VOCABULARY_RELATIONSHIP: { labelKey: 'ui-czna-authorities.vocabularyType.relationship', codeRequired: false },
    VOCABULARY_VARIANT_NAME_TYPE: { labelKey: 'ui-czna-authorities.vocabularyType.variantNameType', codeRequired: false },
    // CZNA-378 — open vocabulary behind 100$c / 400$c ("Doplněk ke jménu"), derived by
    // frequency from 400$c. Free text stays legal (form-config `allowFreeText: true`).
    VOCABULARY_NAME_ADDITION: { labelKey: 'ui-czna-authorities.vocabularyType.nameAddition', codeRequired: false },
    // CZNA-848 — open vocabularies derived from 368$d / 368$c. They replace the five closed 368
    // registers above, which stay listed only so archived terms still render a label.
    VOCABULARY_PERSON_TITLE: { labelKey: 'ui-czna-authorities.vocabularyType.personTitle', codeRequired: false },
    VOCABULARY_OTHER_DESIGNATION: { labelKey: 'ui-czna-authorities.vocabularyType.otherDesignation', codeRequired: false },
    // CZNA-1471 — NK's preset source citations, offered as a quick pick on 670$a
    // (form-config `suggestVocabularyType`); a pick inserts text, nothing is linked.
    VOCABULARY_SOURCE_CITATION: { labelKey: 'ui-czna-authorities.vocabularyType.sourceCitation', codeRequired: false },
};
/**
 * All vocabulary type keys for iteration.
 */
export var VOCABULARY_TYPES = Object.keys(VOCABULARY_TYPE_CONFIG);
// --- Import types ---
export var ImportBatchStatus;
(function (ImportBatchStatus) {
    ImportBatchStatus["RUNNING"] = "RUNNING";
    ImportBatchStatus["COMPLETED"] = "COMPLETED";
    ImportBatchStatus["COMPLETED_WITH_ERRORS"] = "COMPLETED_WITH_ERRORS";
    ImportBatchStatus["FAILED"] = "FAILED";
    ImportBatchStatus["PAUSED"] = "PAUSED";
})(ImportBatchStatus || (ImportBatchStatus = {}));
export var ImportSourceType;
(function (ImportSourceType) {
    ImportSourceType["FILE_UPLOAD"] = "FILE_UPLOAD";
    ImportSourceType["NKP_DOWNLOAD"] = "NKP_DOWNLOAD";
    ImportSourceType["DATABASE_CONNECTION"] = "DATABASE_CONNECTION";
})(ImportSourceType || (ImportSourceType = {}));
export var ImportBatchItemStatus;
(function (ImportBatchItemStatus) {
    ImportBatchItemStatus["IMPORTED"] = "IMPORTED";
    ImportBatchItemStatus["UPDATED"] = "UPDATED";
    ImportBatchItemStatus["SKIPPED"] = "SKIPPED";
    ImportBatchItemStatus["FAILED"] = "FAILED";
})(ImportBatchItemStatus || (ImportBatchItemStatus = {}));
/**
 * CZNA-456 — the reserved template id of a type's packaged form. Every type
 * has exactly one; NK's own templates are created as copies of it.
 *
 * This is the FE half of a two-sided constant: the backend spells it in
 * `ModuleSettingDefaultsLoader.DEFAULT_TEMPLATE_ID` and changeset 101 wrote it
 * into every pre-existing `form_config.{TYPE}` key. `GET /effective/{type}`
 * (no template segment) resolves to it, which is why a caller that passes no
 * template still gets the complete form.
 */
export var DEFAULT_FORM_CONFIG_TEMPLATE_ID = 'default';
/**
 * CZNA-456 — the id constraint from plan R4: lower-case, digits and hyphens,
 * 1–32 chars. Immutable once created (a rename changes `templateLabel`, never
 * the id — otherwise it would be a DELETE + INSERT and the row's history would
 * be lost). Mirrors the backend's own check so the dialog can reject before
 * the round trip; the server remains the authority.
 */
export var FORM_CONFIG_TEMPLATE_ID_PATTERN = /^[a-z0-9-]{1,32}$/;
/** Kinds whose change came from the new packaged default, i.e. is ours to offer. */
export var INCOMING_DRIFT_KINDS = [
    'ADDED_IN_DEFAULT',
    'REMOVED_IN_DEFAULT',
    'UPDATED_IN_DEFAULT',
    'CONFLICT',
    'UNKNOWN_BASE',
];
/** CZNA-456 — compose the `module_setting` key for one named template. */
export var formConfigSettingKey = function (recordType, templateId) {
    if (templateId === void 0) { templateId = DEFAULT_FORM_CONFIG_TEMPLATE_ID; }
    return "form_config.".concat(recordType, ".").concat(templateId);
};
